import { MediaPlayer as dashPlayer, type MediaPlayerClass as DashPlayer } from 'dashjs';
import { getPosterFromStreamId, getSrcFromStreamId, rewriteMpdRelativeUrlsToAbsolute } from './utils';

const PREVIEW_BUFFER_SECONDS = 4;
const MAX_PREVIEW_KBPS = 1000;

type VideoPlayerOptions = {
    limitQualityToElementSize: boolean;
    limitBuffer?: boolean;
    retainWholeClip?: boolean;
    // Drops the audio adaptation set for the life of the player. `mute(false)` cannot bring it back.
    dropAudio?: boolean;
};

export class VideoPlayer {
    private videoElement: HTMLVideoElement;
    private streamId: string;
    private options: VideoPlayerOptions;
    private playerType: 'dash' | 'native';
    private dashPlayer?: DashPlayer;

    constructor(videoElement: HTMLVideoElement, streamId: string, options: VideoPlayerOptions) {
        this.videoElement = videoElement;
        this.streamId = streamId;
        this.options = options;
        this.playerType = this.detectPlayerType();
    }

    private detectPlayerType() {
        // Older iOS devices/versions don't support MediaSource which is used to play DASH stream files
        // If we can't detect support, we'll fall back to the native player as best we can.
        // Realistically web, android, and new ios devices will support DASH.
        let supportsDash = false;
        try {
            const mediaSource = window.ManagedMediaSource ?? window.MediaSource;
            supportsDash = !!mediaSource?.isTypeSupported('video/mp4; codecs="avc1.42E01E"');
        } catch {
            // no-op
        }

        return supportsDash ? 'dash' : 'native';
    }

    private applyPreviewSettings(dash: DashPlayer) {
        if (this.options.limitQualityToElementSize) {
            dash.updateSettings({
                streaming: {
                    abr: {
                        limitBitrateByPortal: true,
                        // Without this the portal filter matches nothing, and dash
                        // falls back to the top rendition.
                        usePixelRatioInLimitBitrateByPortal: true,
                        // FIXME: This feels like a bug in dash.
                        // `limitBitrateByPortal` allows the whole ladder when the element is
                        // narrower than every rung, which a 137.5px card reaches below
                        // devicePixelRatio 1.75. Keep this between the lowest rung and the top
                        // one: too low and dash drops the cap, too high and it caps nothing.
                        maxBitrate: { video: MAX_PREVIEW_KBPS },
                    },
                },
            });
        }

        if (this.options.limitBuffer) {
            dash.updateSettings({
                streaming: {
                    buffer: {
                        bufferTimeDefault: PREVIEW_BUFFER_SECONDS,
                        bufferTimeAtTopQuality: PREVIEW_BUFFER_SECONDS,
                        // A looping player seeks back to 0 forever. Pruning behind the
                        // playhead makes it re-download the whole clip on every pass.
                        bufferToKeep: this.options.retainWholeClip ? Infinity : PREVIEW_BUFFER_SECONDS,
                        bufferPruningInterval: PREVIEW_BUFFER_SECONDS,
                    },
                },
            });
        }

        if (this.options.dropAudio) {
            // dash copies AdaptationSet@mimeType onto each Representation before the filter runs.
            dash.registerCustomCapabilitiesFilter(representation => !representation.mimeType?.startsWith('audio/'));
        }
    }

    public init(onCanPlay: () => void) {
        if (this.playerType === 'dash') {
            const dash = dashPlayer().create();

            this.applyPreviewSettings(dash);

            dash.addResponseInterceptor(response => {
                // This modifies the response to ensure the segment urls are absolute
                // because relative urls become a problem in capacitor
                if (response.url?.includes('mpd') && response.data && typeof response.data === 'string') {
                    const patched = rewriteMpdRelativeUrlsToAbsolute(response.data, new URL(this.getSrc()));

                    return Promise.resolve({
                        ...response,
                        data: patched,
                    });
                }
                return Promise.resolve(response);
            });
            dash.on(dashPlayer.events.CAN_PLAY, onCanPlay);
            dash.on(dashPlayer.events.PLAYBACK_NOT_ALLOWED, () => {
                dash.setMute(true);
                dash.play();
            });
            dash.initialize(this.videoElement, this.getSrc(), false);

            this.dashPlayer = dash;
            this.videoElement.poster = getPosterFromStreamId(this.streamId);
        } else {
            this.videoElement.onloadedmetadata = onCanPlay;
            this.videoElement.oncanplay = onCanPlay;
            this.videoElement.src = this.getSrc();
            this.videoElement.preload = 'auto';
            this.videoElement.load();
            this.videoElement.poster = getPosterFromStreamId(this.streamId);
        }
    }

    public play(needsNativeLoad = false) {
        if (this.dashPlayer) {
            this.dashPlayer.play();
        } else {
            if (needsNativeLoad) this.videoElement.load();
            this.videoElement.play();
        }
    }

    public pause() {
        if (this.dashPlayer) {
            this.dashPlayer.pause();
        } else {
            this.videoElement.pause();
        }
    }

    public seek(seconds: number) {
        if (this.dashPlayer) {
            this.dashPlayer.seek(seconds);
        } else {
            this.videoElement.currentTime = seconds;
        }
    }

    public mute(muted: boolean) {
        if (this.dashPlayer) {
            this.dashPlayer.setMute(muted);
        } else {
            this.videoElement.muted = muted;
        }
    }

    public enableCaptions(enable: boolean) {
        if (this.dashPlayer) {
            this.dashPlayer.enableText(enable);
        } else {
            // Native: assume the first track is the captions track
            const tracks = this.videoElement.textTracks;
            for (let i = 0; i < tracks.length; i++) {
                tracks[i].mode = enable ? 'showing' : 'disabled';
            }
        }
    }

    public getCurrentTime() {
        return this.dashPlayer ? this.dashPlayer.time() : this.videoElement.currentTime;
    }

    public getDuration() {
        return this.dashPlayer ? this.dashPlayer.duration() : this.videoElement.duration;
    }

    public isPaused() {
        return this.dashPlayer ? this.dashPlayer.isPaused() : this.videoElement.paused;
    }

    public destroy() {
        if (this.dashPlayer) {
            this.dashPlayer.destroy();
        }
        this.videoElement.oncanplay = null;
        this.videoElement.onloadedmetadata = null;
        this.videoElement.removeAttribute('src');
        this.videoElement.load();
    }

    public getSrc() {
        if (this.playerType === 'dash') return getSrcFromStreamId(this.streamId, 'mpd');

        return getSrcFromStreamId(this.streamId, 'm3u8');
    }
}
